<template>
  <div class="flex items-center space-x-4">
    <!-- Period Buttons -->
    <div class="flex items-center space-x-2">
      <button
        v-for="option in periodOptions"
        :key="option.value"
        @click="selectPeriod(option.value)"
        :class="[
          'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
          period === option.value && !customRangeActive
            ? 'bg-gray-900 text-white'
            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
        ]">
        {{ option.label }}
      </button>
    </div>

    <!-- Custom Date Range -->
    <div class="flex items-center space-x-2">
      <button
        @click="toggleCustomRange"
        :class="[
          'px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center',
          customRangeActive
            ? 'bg-gray-900 text-white'
            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
        ]">
        <svg class="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        Custom
      </button>

      <div v-if="showDatePicker" class="flex items-center space-x-2">
        <input
          type="date"
          v-model="fromDate"
          :max="toDate || today"
          class="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500">
        <span class="text-gray-500">to</span>
        <input
          type="date"
          v-model="toDate"
          :min="fromDate"
          :max="today"
          class="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500">
        <button
          @click="applyCustomRange"
          :disabled="!fromDate || !toDate"
          class="px-3 py-1 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed">
          Apply
        </button>
      </div>
    </div>

    <!-- Compare Toggle -->
    <div class="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-200">
      <label class="flex items-center cursor-pointer">
        <input
          type="checkbox"
          v-model="compareEnabled"
          @change="toggleCompare"
          class="sr-only peer">
        <div class="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-gray-900" />
        <span class="ml-2 text-sm text-gray-600">Compare</span>
      </label>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TimeSelector',

  props: {
    modelValue: {
      type: String,
      default: '7d'
    },
    compare: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue', 'update:compare', 'period-change', 'custom-range'],

  data() {
    return {
      period: this.modelValue,
      compareEnabled: this.compare,
      showDatePicker: false,
      customRangeActive: false,
      fromDate: null,
      toDate: null,
      periodOptions: [
        { value: 'today', label: 'Today' },
        { value: '7d', label: '7D' },
        { value: '30d', label: '30D' },
        { value: '90d', label: '90D' }
      ]
    }
  },

  computed: {
    today() {
      return new Date().toISOString().split('T')[0]
    }
  },

  watch: {
    modelValue(newVal) {
      this.period = newVal
    },
    compare(newVal) {
      this.compareEnabled = newVal
    }
  },

  methods: {
    selectPeriod(value) {
      this.period = value
      this.customRangeActive = false
      this.showDatePicker = false
      this.$emit('update:modelValue', value)
      this.$emit('period-change', { period: value })
    },

    toggleCustomRange() {
      this.showDatePicker = !this.showDatePicker
      if (!this.showDatePicker) {
        this.customRangeActive = false
      }
    },

    applyCustomRange() {
      if (this.fromDate && this.toDate) {
        this.customRangeActive = true
        this.period = 'custom'
        this.$emit('custom-range', {
          from: this.fromDate,
          to: this.toDate
        })
      }
    },

    toggleCompare() {
      this.$emit('update:compare', this.compareEnabled)
      this.$emit('period-change', {
        period: this.period,
        compare: this.compareEnabled
      })
    }
  }
}
</script>
